import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSiteConfig, SiteConfig, magicalFonts, defaultSiteConfig, API_BASE, resolveUploadUrl, PROJECT_ROOT } from '../utils/siteConfig';
import * as Icons from 'lucide-react';
import { adminFetch, adminSession, setAdminCsrf } from '../utils/api';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Deep-clone via JSON (safe for plain config data) */
const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

// Helper to parse YouTube ID from any standard YouTube URL
function getYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
}

const adminSocialIconMap: Record<string, any> = {
  LinkedIn: Icons.Linkedin,
  YouTube: Icons.Youtube,
  Facebook: Icons.Facebook,
  Twitter: Icons.Twitter,
  Instagram: Icons.Instagram,
  GitHub: Icons.Github,
  Meta: Icons.Facebook,
  TikTok: Icons.Music,
  Pinterest: Icons.Pin
};

const adminBrandColors: Record<string, string> = {
  LinkedIn: 'text-[#0077b5]',
  YouTube: 'text-[#ff0000]',
  Facebook: 'text-[#1877f2]',
  Twitter: 'text-[#1da1f2]',
  Instagram: 'text-[#e1306c]',
  GitHub: 'text-[#181717]',
  Meta: 'text-[#0668e1]',
  TikTok: 'text-[#000000]',
  Pinterest: 'text-[#bd081c]'
};

// ─── component ───────────────────────────────────────────────────────────────
export function AdminDashboard() {
  const navigate = useNavigate();
  const { config, updateConfig } = useSiteConfig();

  // local working copy – only committed to context/localStorage on change
  const [draft, setDraft] = useState<SiteConfig>(() => clone(config));
  const [savedBanner, setSavedBanner] = useState(false);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  type TabId = 'branding' | 'home' | 'about' | 'services' | 'portfolio' | 'testimonials' | 'customPages' | 'footerContacts' | 'emailSmtp';
  const [activeTab, setActiveTab] = useState<TabId>('branding');

  // SMTP Testing states
  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState('');

  // Video upload states
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadError, setVideoUploadError] = useState('');
  const [tempVideoUrl, setTempVideoUrl] = useState('');

  // Background image upload states
  const [bgUploading, setBgUploading] = useState(false);
  const [bgUploadError, setBgUploadError] = useState('');

  const handleSendTestEmail = async () => {
    if (!testEmail) return;
    setTestStatus('loading');
    setTestError('');
    try {
      const response = await adminFetch(`${API_BASE}/send-email.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'BigGroww Admin',
          email: testEmail,
          phone: '',
          subject: 'SMTP Configuration Test Successful',
          message: 'This is a test email sent from the BigGroww Admin Dashboard to verify that your SMTP mail server settings are fully functional.',
          isTest: true,
          testRecipient: testEmail
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setTestStatus('success');
      } else {
        setTestStatus('error');
        setTestError(data.error || 'Failed to send test email.');
      }
    } catch (e: any) {
      console.error(e);
      setTestStatus('error');
      setTestError('A network error occurred. Please save settings and try again.');
    }
  };

  // ── auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    adminSession(API_BASE).then(isAuthenticated => { if (!isAuthenticated) navigate('/admin'); }).catch(() => navigate('/admin'));
  }, [navigate]);

  // ── keep draft in sync if context changes externally ─────────────────────
  useEffect(() => {
    setDraft(clone(config));
  }, [config]);

  const handleLogout = () => {
    adminFetch(`${API_BASE}/auth.php?action=logout`, { method: 'POST' }).catch(() => undefined);
    setAdminCsrf(null);
    navigate('/admin');
  };

  // ── commit every change immediately ────────────────────────────────────────
  const commit = (updated: SiteConfig) => {
    setDraft(updated);
    updateConfig(updated);          // persists to localStorage + DOM variables
    if (bannerTimer.current) clearTimeout(bannerTimer.current);
    setSavedBanner(true);
    bannerTimer.current = setTimeout(() => setSavedBanner(false), 2000);
  };

  // ── theme helpers ───────────────────────────────────────────────────────────
  const setThemeField = (field: keyof SiteConfig['theme'], value: string) => {
    commit({ ...draft, theme: { ...draft.theme, [field]: value } });
  };

  // ── home helpers ────────────────────────────────────────────────────────────
  const setHeroField = (field: keyof SiteConfig['home']['hero'], value: string) => {
    commit({ ...draft, home: { ...draft.home, hero: { ...draft.home.hero, [field]: value } } });
  };

  const setStatField = (index: number, field: 'number' | 'label', value: string) => {
    const stats = clone(draft.home.stats);
    stats[index] = { ...stats[index], [field]: value };
    commit({ ...draft, home: { ...draft.home, stats } });
  };

  const addHeroLogo = () => {
    const clientLogos = [...(draft.home.hero.clientLogos || []), { name: 'New Client', image: '' }];
    commit({ ...draft, home: { ...draft.home, hero: { ...draft.home.hero, clientLogos } } });
  };

  const deleteHeroLogo = (index: number) => {
    const clientLogos = (draft.home.hero.clientLogos || []).filter((_, i) => i !== index);
    commit({ ...draft, home: { ...draft.home, hero: { ...draft.home.hero, clientLogos } } });
  };

  const setHeroLogoField = (index: number, field: 'name' | 'image', value: string) => {
    const clientLogos = clone(draft.home.hero.clientLogos || []);
    clientLogos[index] = { ...clientLogos[index], [field]: value };
    commit({ ...draft, home: { ...draft.home, hero: { ...draft.home.hero, clientLogos } } });
  };

  const handleHeroLogoUpload = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 400; // Logos don't need to be huge
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
        else       { if (h > MAX) { w *= MAX / h; h = MAX; } }
        canvas.width = Math.round(w); canvas.height = Math.round(h);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        setHeroLogoField(index, 'image', canvas.toDataURL('image/png'));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  // ── about helpers ───────────────────────────────────────────────────────────
  const setAboutDataField = (field: keyof Omit<SiteConfig['about'], 'coreValues' | 'promise'>, key: 'text' | 'image1' | 'image2', value: string) => {
    const raw = draft.about[field];
    // Normalize: if the stored value is still a plain string (old format), convert to object first
    const current: { text: string; image1: string; image2: string } =
      (typeof raw === 'object' && raw !== null)
        ? (raw as { text: string; image1: string; image2: string })
        : { text: typeof raw === 'string' ? raw : '', image1: '', image2: '' };
    commit({ ...draft, about: { ...draft.about, [field]: { ...current, [key]: value } } });
  };

  const handleAboutImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: keyof Omit<SiteConfig['about'], 'coreValues' | 'promise'>, key: 'image1' | 'image2') => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 800;
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
        else       { if (h > MAX) { w *= MAX / h; h = MAX; } }
        canvas.width = Math.round(w); canvas.height = Math.round(h);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        setAboutDataField(field, key, canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const setPromiseField = (field: keyof SiteConfig['about']['promise'], value: string) => {
    commit({ ...draft, about: { ...draft.about, promise: { ...draft.about.promise, [field]: value } } });
  };

  const addCoreValue = () => {
    const coreValues = [...draft.about.coreValues, { title: 'New Value', description: 'Brief description.', icon: 'Zap', color: 'from-blue-500 to-cyan-500' }];
    commit({ ...draft, about: { ...draft.about, coreValues } });
  };

  const deleteCoreValue = (i: number) => {
    const coreValues = draft.about.coreValues.filter((_, idx) => idx !== i);
    commit({ ...draft, about: { ...draft.about, coreValues } });
  };

  const setCoreValueField = (i: number, field: string, value: string) => {
    const coreValues = clone(draft.about.coreValues);
    coreValues[i] = { ...coreValues[i], [field]: value };
    commit({ ...draft, about: { ...draft.about, coreValues } });
  };

  // ── footer helpers ──────────────────────────────────────────────────────────
  const setFooterField = (field: keyof Omit<SiteConfig['footer'], 'sections'>, value: string) => {
    commit({ ...draft, footer: { ...draft.footer, [field]: value } });
  };

  const setFooterSectionTitle = (secIdx: number, value: string) => {
    const sections = clone(draft.footer.sections);
    sections[secIdx] = { ...sections[secIdx], title: value };
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  const setFooterLinkField = (secIdx: number, linkIdx: number, field: 'label' | 'href', value: string) => {
    const sections = clone(draft.footer.sections);
    sections[secIdx].links[linkIdx] = { ...sections[secIdx].links[linkIdx], [field]: value };
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  const addFooterLink = (secIdx: number) => {
    const sections = clone(draft.footer.sections);
    sections[secIdx].links.push({ label: 'New Link', href: '/' });
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  const deleteFooterLink = (secIdx: number, linkIdx: number) => {
    const sections = clone(draft.footer.sections);
    sections[secIdx].links.splice(linkIdx, 1);
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  const addFooterSection = () => {
    const sections = [...draft.footer.sections, { title: 'New Section', links: [{ label: 'Link', href: '/' }] }];
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  const deleteFooterSection = (secIdx: number) => {
    const sections = draft.footer.sections.filter((_, i) => i !== secIdx);
    commit({ ...draft, footer: { ...draft.footer, sections } });
  };

  // ── contact helpers ─────────────────────────────────────────────────────────
  const setContactField = (field: keyof Omit<SiteConfig['contact'], 'socials'>, value: string) => {
    commit({ ...draft, contact: { ...draft.contact, [field]: value } });
  };

  const setSocialField = (i: number, field: 'platform' | 'url', value: string) => {
    const socials = clone(draft.contact.socials);
    socials[i] = { ...socials[i], [field]: value };
    commit({ ...draft, contact: { ...draft.contact, socials } });
  };

  const addSocial = () => {
    const socials = [...draft.contact.socials, { platform: 'Twitter', url: 'https://twitter.com' }];
    commit({ ...draft, contact: { ...draft.contact, socials } });
  };

  const deleteSocial = (i: number) => {
    const socials = draft.contact.socials.filter((_, idx) => idx !== i);
    commit({ ...draft, contact: { ...draft.contact, socials } });
  };

  type ArrayKey = 'services' | 'additionalServices' | 'portfolio' | 'testimonials' | 'customPages';

  const addItem = (key: ArrayKey, item: any) => {
    const list = draft[key] ? (draft[key] as any[]) : [];
    commit({ ...draft, [key]: [...list, item] });
  };

  const deleteItem = (key: ArrayKey, i: number) => {
    const list = draft[key] ? (draft[key] as any[]) : [];
    commit({ ...draft, [key]: list.filter((_: any, idx: number) => idx !== i) });
  };

  const setItemField = (key: ArrayKey, i: number, field: string, value: any) => {
    const list = draft[key] ? clone(draft[key] as any[]) : [];
    list[i] = { ...list[i], [field]: value };
    commit({ ...draft, [key]: list });
  };

  // ── image upload with compression ──────────────────────────────────────────
  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: 'services' | 'portfolio' | 'testimonials',
    index: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 800;
        let w = img.width, h = img.height;
        if (w > h) { if (w > MAX) { h *= MAX / w; w = MAX; } }
        else       { if (h > MAX) { w *= MAX / h; h = MAX; } }
        canvas.width = Math.round(w); canvas.height = Math.round(h);
        canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
        setItemField(key, index, 'image', canvas.toDataURL('image/jpeg', 0.72));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  // ── reset to defaults ───────────────────────────────────────────────────────
  const resetToDefaults = () => {
    if (window.confirm('Reset ALL website content to factory defaults? This cannot be undone.')) {
      commit(clone(defaultSiteConfig));
    }
  };

  // ── icon list ───────────────────────────────────────────────────────────────
  const iconNames = [
    'Sparkles', 'Smartphone', 'Globe', 'Megaphone', 'Target', 'Award', 'Palette',
    'Zap', 'Briefcase', 'Users', 'Heart', 'Shield', 'TrendingUp', 'CheckCircle',
    'Camera', 'Code', 'Video', 'Star', 'Layers', 'Mail', 'Phone', 'MapPin',
  ];

  // ── sidebar tabs ────────────────────────────────────────────────────────────
  const tabs: { id: TabId; label: string; icon: any }[] = [
    { id: 'branding',       label: 'Theme & Fonts',     icon: Icons.Palette },
    { id: 'home',           label: 'Home Page Hero',    icon: Icons.Home },
    { id: 'about',          label: 'About Page',        icon: Icons.Info },
    { id: 'services',       label: 'Services',          icon: Icons.Layers },
    { id: 'portfolio',      label: 'Portfolio',         icon: Icons.Briefcase },
    { id: 'testimonials',   label: 'Testimonials',      icon: Icons.MessageSquare },
    { id: 'customPages',    label: 'Dynamic Pages',     icon: Icons.FileText },
    { id: 'footerContacts', label: 'Footer & Contacts', icon: Icons.Phone },
    { id: 'emailSmtp',      label: 'Email / SMTP',      icon: Icons.Mail },
  ];

  // ── shared input classes ────────────────────────────────────────────────────
  const inp = 'w-full px-4 py-2.5 border border-slate-200 bg-white rounded-xl text-sm outline-none focus:ring-2 focus:ring-primary/30 transition';
  const lbl = 'block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5';

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── TOP BAR ─────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-slate-200 py-3 px-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center gap-4 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center justify-between sm:justify-start gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ background: draft.theme.primaryColor }}>
              <Icons.LayoutDashboard size={20} />
            </div>
            <div>
              <h1 className="text-base sm:text-lg font-black text-slate-900 leading-tight">BigGroww CMS</h1>
              <p className="text-[10px] sm:text-xs text-slate-400">Website Content & Theme Manager</p>
            </div>
          </div>
          {savedBanner && (
            <span className="sm:hidden flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full text-[10px] font-bold border border-emerald-200">
              <Icons.CheckCircle size={10} /> Saved!
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 sm:gap-3 w-full sm:w-auto">
          {savedBanner && (
            <span className="hidden sm:flex items-center gap-1.5 text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full text-xs font-bold border border-emerald-200 animate-pulse">
              <Icons.CheckCircle size={13} /> Saved!
            </span>
          )}
          <button onClick={() => window.open(import.meta.env.BASE_URL, '_blank')} className="flex items-center gap-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold text-slate-600 hover:text-primary bg-slate-100 hover:bg-slate-200 transition-all">
            <Icons.ExternalLink size={13} /> Preview Site
          </button>
          <button onClick={resetToDefaults} className="flex items-center gap-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold text-orange-500 hover:bg-orange-50 border border-orange-200 transition-all">
            <Icons.RotateCcw size={13} /> Reset
          </button>
          <button onClick={handleLogout} className="flex items-center gap-1 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-bold text-red-500 hover:bg-red-50 border border-red-200 transition-all">
            <Icons.LogOut size={13} /> Logout
          </button>
        </div>
      </header>

      {/* ── WORKSPACE ───────────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">

        {/* ── SIDEBAR ────────────────────────────────────────────────────── */}
        <aside className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-slate-200 p-3 md:p-4 flex flex-row md:flex-col gap-1 overflow-x-auto md:overflow-y-auto shrink-0">
          {tabs.map(({ id, label, icon: TabIcon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 md:gap-3 px-3 py-2 md:px-4 md:py-2.5 rounded-xl font-semibold text-xs md:text-sm transition-all shrink-0 ${
                activeTab === id
                  ? 'text-white shadow-md'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              style={activeTab === id ? { background: draft.theme.primaryColor } : {}}
            >
              <TabIcon size={15} />
              <span>{label}</span>
            </button>
          ))}
        </aside>

        {/* ── MAIN PANEL ─────────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl md:rounded-3xl border border-slate-200 shadow-sm p-4 md:p-8">

            {/* ════════════════════════════════════════════════════════════
                BRANDING TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'branding' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Theme & Fonts</h2>
                  <p className="text-sm text-slate-500 mt-1">Changes apply to the live website instantly — no reload needed.</p>
                </div>

                {/* Color Pickers */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    { label: 'Primary Color', field: 'primaryColor' as const, val: draft.theme.primaryColor },
                    { label: 'Accent Color',  field: 'accentColor'  as const, val: draft.theme.accentColor  },
                    { label: 'Highlight Color', field: 'highlightColor' as const, val: draft.theme.highlightColor },
                  ].map(({ label, field, val }) => (
                    <div key={field} className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
                      <label className={lbl}>{label}</label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={val}
                          onChange={e => setThemeField(field, e.target.value)}
                          className="w-12 h-12 rounded-lg border border-slate-300 cursor-pointer p-0.5"
                        />
                        <input
                          type="text"
                          value={val}
                          maxLength={7}
                          onChange={e => setThemeField(field, e.target.value)}
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono"
                        />
                      </div>
                      {/* live swatch */}
                      <div className="mt-3 h-2 rounded-full" style={{ background: val }} />
                    </div>
                  ))}
                </div>

                {/* Font Family */}
                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                  <label className={lbl}>Website Font Family</label>
                  <p className="text-xs text-slate-400 mb-3">20 magical Google Fonts — loaded dynamically, applied site-wide instantly.</p>
                  <select
                    value={draft.theme.fontFamily}
                    onChange={e => setThemeField('fontFamily', e.target.value)}
                    className="w-full md:w-80 px-4 py-3 bg-white border border-slate-200 rounded-xl text-base font-medium outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {magicalFonts.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  {/* font preview */}
                  <div className="mt-5 p-6 bg-white border border-slate-200 rounded-xl">
                    <span className="text-xs text-slate-400 uppercase tracking-widest block mb-2">Live Preview</span>
                    <h3 style={{ fontFamily: `'${draft.theme.fontFamily}', sans-serif` }} className="text-2xl font-black text-slate-900 mb-1">
                      We Build Brands. Creating Impact.
                    </h3>
                    <p style={{ fontFamily: `'${draft.theme.fontFamily}', sans-serif` }} className="text-slate-500 text-sm leading-relaxed">
                      This preview reflects exactly how your typography will appear across all pages of the website.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                HOME TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'home' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Home Page Hero</h2>
                  <p className="text-sm text-slate-500 mt-1">Edit headline, buttons, subtitle, and the 4 statistic counters.</p>
                </div>

                <div>
                  <label className={lbl}>Trust Badge Text</label>
                  <input className={inp} value={draft.home.hero.trustBadgeText}
                    onChange={e => setHeroField('trustBadgeText', e.target.value)} />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Headline Line 1</label>
                    <input className={inp} value={draft.home.hero.headlineLine1}
                      onChange={e => setHeroField('headlineLine1', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Headline Line 2 (Gradient)</label>
                    <input className={inp} value={draft.home.hero.headlineLine2}
                      onChange={e => setHeroField('headlineLine2', e.target.value)} />
                  </div>
                </div>

                <div>
                  <label className={lbl}>Subtitle / Description</label>
                  <textarea className={inp} rows={3} value={draft.home.hero.subtitle}
                    onChange={e => setHeroField('subtitle', e.target.value)} />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>Primary Button Label</label>
                    <input className={inp} value={draft.home.hero.primaryBtnText}
                      onChange={e => setHeroField('primaryBtnText', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Secondary Button Label</label>
                    <input className={inp} value={draft.home.hero.secondaryBtnText}
                      onChange={e => setHeroField('secondaryBtnText', e.target.value)} />
                  </div>
                </div>

                {/* Hero Video Background */}
                <div className="border-t border-slate-100 pt-6">
                  <h3 className="text-base font-bold text-slate-900 mb-2">Hero Video Background</h3>
                  <p className="text-xs text-slate-500 mb-4">
                    Upload an MP4/WebM video or specify an external URL to use as the hero background.
                    If no video is set, the default animated shader background will be shown.
                  </p>
                  
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                    {draft.home.hero.videoUrl ? (
                      <div className="space-y-4">
                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between bg-white border border-slate-200 rounded-xl p-4">
                          <div className="space-y-1">
                            <span className="text-xs text-slate-400 font-medium">Current Video Source</span>
                            <div className="text-sm font-semibold truncate max-w-md text-slate-700">
                              {draft.home.hero.videoUrl}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                if (draft.home.hero.videoUrl) {
                                  window.open(draft.home.hero.videoUrl, '_blank');
                                }
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition"
                            >
                              <Icons.ExternalLink size={12} /> Open Link
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm("Are you sure you want to remove the video background?")) {
                                  const oldUrl = draft.home.hero.videoUrl;
                                  setHeroField('videoUrl', '');
                                  
                                  // Call API to delete file if it was a local upload
                                  if (oldUrl?.startsWith('/biggrow/uploads/') || oldUrl?.startsWith('/uploads/')) {
                                    try {
                                      await adminFetch(`${API_BASE}/delete-video.php`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ url: oldUrl })
                                      });
                                    } catch (err) {
                                      console.error("Error deleting video file:", err);
                                    }
                                  }
                                }
                              }}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg transition border border-red-200"
                            >
                              <Icons.Trash2 size={12} /> Delete Video
                            </button>
                          </div>
                        </div>

                        {/* Video preview player */}
                        <div className="aspect-video max-w-md rounded-xl overflow-hidden border border-slate-200 bg-black relative">
                          {getYouTubeId(draft.home.hero.videoUrl) ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${getYouTubeId(draft.home.hero.videoUrl)}?autoplay=0&mute=1&controls=1`}
                              frameBorder="0"
                              allow="autoplay; encrypted-media"
                              allowFullScreen
                              title="Hero Video Background Preview"
                              className="w-full h-full"
                            />
                          ) : (
                            <video
                              src={resolveUploadUrl(draft.home.hero.videoUrl)}
                              controls
                              muted
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {/* Option 1: File Upload */}
                        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between space-y-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-800">Upload Video File</h4>
                            <p className="text-xs text-slate-400 mt-1">Select an MP4, WebM, or OGG file from your computer.</p>
                          </div>
                          
                          <div className="relative">
                            <input
                              type="file"
                              accept="video/*"
                              id="video-upload-input"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setVideoUploading(true);
                                setVideoUploadError('');
                                
                                const formData = new FormData();
                                formData.append('video', file);
                                
                                try {
                                  const response = await adminFetch(`${API_BASE}/upload-video.php`, {
                                    method: 'POST',
                                    body: formData
                                  });
                                  const data = await response.json();
                                  if (response.ok && data.success) {
                                    setHeroField('videoUrl', data.url);
                                  } else {
                                    setVideoUploadError(data.error || 'Failed to upload video.');
                                  }
                                } catch (err) {
                                  console.error(err);
                                  setVideoUploadError('A network error occurred during upload.');
                                } finally {
                                  setVideoUploading(false);
                                }
                              }}
                              className="hidden"
                            />
                            <button
                              disabled={videoUploading}
                              type="button"
                              onClick={() => document.getElementById('video-upload-input')?.click()}
                              className="w-full py-3 px-4 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 font-bold rounded-xl text-sm transition flex items-center justify-center gap-2"
                            >
                              {videoUploading ? (
                                <>
                                  <span className="w-4 h-4 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Icons.UploadCloud size={16} />
                                  Choose Video File
                                </>
                              )}
                            </button>
                          </div>
                          
                          {videoUploadError && (
                            <div className="text-xs text-red-500 font-semibold mt-1">
                              {videoUploadError}
                            </div>
                          )}
                        </div>

                        {/* Option 2: External Video URL */}
                        <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col justify-between space-y-4">
                          <div>
                            <h4 className="text-sm font-bold text-slate-800">External Video URL</h4>
                            <p className="text-xs text-slate-400 mt-1">Paste a link to a self-hosted video or a direct video file link.</p>
                          </div>
                          
                          <div className="space-y-2">
                            <input
                              type="url"
                              placeholder="https://example.com/video.mp4"
                              value={tempVideoUrl}
                              onChange={(e) => setTempVideoUrl(e.target.value)}
                              className={inp}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                if (tempVideoUrl) {
                                  setHeroField('videoUrl', tempVideoUrl);
                                  setTempVideoUrl('');
                                }
                              }}
                              className="w-full py-2.5 px-4 bg-primary text-white hover:bg-primary/90 font-bold rounded-xl text-sm transition"
                              style={{ background: draft.theme.primaryColor }}
                            >
                              Apply URL
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Client Logos */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-base font-bold text-slate-900">Client Project Logos (Carousel)</h3>
                    <button onClick={addHeroLogo}
                      className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl font-bold border transition-all"
                      style={{ color: draft.theme.primaryColor, borderColor: draft.theme.primaryColor + '44', background: draft.theme.primaryColor + '0f' }}>
                      <Icons.Plus size={15} /> Add Logo
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {(draft.home.hero.clientLogos || []).map((logo, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col gap-3 relative group">
                        <button onClick={() => deleteHeroLogo(i)}
                          className="absolute top-3 right-3 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all z-10 bg-white p-1 rounded-md shadow-sm border border-slate-100">
                          <Icons.Trash2 size={14} />
                        </button>
                        
                        <div className="flex gap-4">
                          <div className="w-16 h-16 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-white overflow-hidden shrink-0 relative cursor-pointer"
                            onClick={() => document.getElementById(`logo-upload-${i}`)?.click()}
                          >
                            {logo.image ? (
                              <img src={resolveUploadUrl(logo.image)} alt={logo.name} className="w-full h-full object-contain p-1" />
                            ) : (
                              <Icons.ImagePlus size={20} className="text-slate-400" />
                            )}
                            <input
                              type="file"
                              id={`logo-upload-${i}`}
                              accept="image/*"
                              className="hidden"
                              onChange={e => handleHeroLogoUpload(e, i)}
                            />
                          </div>
                          
                          <div className="flex-1 space-y-1">
                            <label className={lbl}>Company Name</label>
                            <input className={inp} value={logo.name}
                              onChange={e => setHeroLogoField(i, 'name', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="border-t border-slate-100 pt-6">
                  <h3 className="text-base font-bold text-slate-900 mb-4">Statistic Counters (shown below hero)</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {draft.home.stats.map((stat, i) => (
                      <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                        <div>
                          <label className={lbl}>Number</label>
                          <input className={inp} value={stat.number}
                            onChange={e => setStatField(i, 'number', e.target.value)} />
                        </div>
                        <div>
                          <label className={lbl}>Label</label>
                          <input className={inp} value={stat.label}
                            onChange={e => setStatField(i, 'label', e.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                ABOUT TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'about' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">About Page</h2>
                  <p className="text-sm text-slate-500 mt-1">Edit all text blocks, mission, vision, core values, and promise banner.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  {(
                    [
                      ['whoWeAre',  'Who We Are'],
                      ['expertise', 'Our Expertise'],
                      ['partners',  'Our Partners'],
                      ['approach',  'Our Approach'],
                      ['mission',   'Our Mission'],
                      ['vision',    'Our Vision'],
                    ] as [keyof Omit<SiteConfig['about'], 'coreValues' | 'promise'>, string][]
                  ).map(([field, label]) => {
                    const rawData = draft.about[field];
                    const data = (typeof rawData === 'object' && rawData !== null) 
                      ? (rawData as { text: string; image1: string; image2: string }) 
                      : { text: typeof rawData === 'string' ? rawData : '', image1: '', image2: '' };
                      
                    return (
                      <div key={field} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl space-y-4">
                        <div>
                          <label className={lbl}>{label}</label>
                          <textarea className={inp} rows={4}
                            value={data.text || ''}
                            onChange={e => setAboutDataField(field, 'text', e.target.value)} />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 pt-2">
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Primary Image</label>
                            <div className="relative border-2 border-dashed border-slate-300 rounded-xl bg-white overflow-hidden group hover:border-primary transition-colors h-24">
                              {data.image1 ? (
                                <img src={resolveUploadUrl(data.image1)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors">
                                  <Icons.Image size={20} />
                                </div>
                              )}
                              <input type="file" accept="image/*" onChange={(e) => handleAboutImageUpload(e, field, 'image1')}
                                className="absolute inset-0 opacity-0 cursor-pointer" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-500 mb-1">Hover Image</label>
                            <div className="relative border-2 border-dashed border-slate-300 rounded-xl bg-white overflow-hidden group hover:border-primary transition-colors h-24">
                              {data.image2 ? (
                                <img src={resolveUploadUrl(data.image2)} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors">
                                  <Icons.Image size={20} />
                                </div>
                              )}
                              <input type="file" accept="image/*" onChange={(e) => handleAboutImageUpload(e, field, 'image2')}
                                className="absolute inset-0 opacity-0 cursor-pointer" />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Promise */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-900">Our Promise Banner</h3>
                  <div>
                    <label className={lbl}>Badge Text</label>
                    <input className={inp} value={draft.about.promise.tagline}
                      onChange={e => setPromiseField('tagline', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Promise Title</label>
                    <input className={inp} value={draft.about.promise.text}
                      onChange={e => setPromiseField('text', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Final Quote (italic gradient)</label>
                    <input className={inp} value={draft.about.promise.quote}
                      onChange={e => setPromiseField('quote', e.target.value)} />
                  </div>
                </div>

                {/* Core Values */}
                <div className="border-t border-slate-100 pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-slate-900">Core Values Cards</h3>
                    <button onClick={addCoreValue}
                      className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl font-bold border transition-all"
                      style={{ color: draft.theme.primaryColor, borderColor: draft.theme.primaryColor + '44', background: draft.theme.primaryColor + '0f' }}>
                      <Icons.Plus size={15} /> Add Card
                    </button>
                  </div>
                  <div className="space-y-4">
                    {draft.about.coreValues.map((v, i) => (
                      <div key={i} className="p-5 bg-slate-50 rounded-2xl border border-slate-200 relative group">
                        <button onClick={() => deleteCoreValue(i)}
                          className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                          <Icons.Trash2 size={16} />
                        </button>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className={lbl}>Title</label>
                            <input className={inp} value={v.title} onChange={e => setCoreValueField(i, 'title', e.target.value)} />
                          </div>
                          <div>
                            <label className={lbl}>Lucide Icon</label>
                            <select className={inp} value={v.icon} onChange={e => setCoreValueField(i, 'icon', e.target.value)}>
                              {iconNames.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={lbl}>Card Gradient CSS</label>
                            <input className={inp} value={v.color} placeholder="from-blue-500 to-cyan-500"
                              onChange={e => setCoreValueField(i, 'color', e.target.value)} />
                          </div>
                          <div className="md:col-span-3">
                            <label className={lbl}>Description</label>
                            <textarea className={inp} rows={2} value={v.description}
                              onChange={e => setCoreValueField(i, 'description', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                SERVICES TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'services' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900">Services</h2>
                    <p className="text-sm text-slate-500 mt-1">Add, edit, or delete services displayed on the Services page and Home page.</p>
                  </div>
                  <button onClick={() => addItem('services', { title: 'New Service', description: 'Brief description.', icon: 'Zap', image: '' })}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm text-white shadow-md transition-all"
                    style={{ background: draft.theme.primaryColor }}>
                    <Icons.Plus size={15} /> Add Service
                  </button>
                </div>
                <div className="space-y-4">
                  {draft.services.map((svc, i) => (
                    <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl relative group">
                      <button onClick={() => deleteItem('services', i)}
                        className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                        <Icons.Trash2 size={16} />
                      </button>
                      <div className="flex gap-5">
                        {/* Service Image upload */}
                        <div className="shrink-0 flex flex-col gap-1">
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Service Image</label>
                          <label className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary overflow-hidden relative">
                            {svc.image
                              ? <img src={resolveUploadUrl(svc.image)} className="w-full h-full object-cover absolute inset-0" alt="" />
                              : <><Icons.ImagePlus size={24} className="text-slate-400 mb-1" /><span className="text-xs text-slate-400">Upload</span></>}
                            <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-bold opacity-0 hover:opacity-100 transition-all">Change</span>
                            <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'services', i)} />
                          </label>
                          {svc.image && (
                            <button onClick={() => setItemField('services', i, 'image', '')} className="text-xs text-red-400 hover:text-red-600 text-center mt-1">Remove</button>
                          )}
                        </div>
                        {/* Fields */}
                        <div className="flex-1 grid md:grid-cols-2 gap-4">
                          <div className="md:col-span-1">
                            <label className={lbl}>Title</label>
                            <input className={inp} value={svc.title} onChange={e => setItemField('services', i, 'title', e.target.value)} />
                          </div>
                          <div>
                            <label className={lbl}>Icon (fallback)</label>
                            <select className={inp} value={svc.icon} onChange={e => setItemField('services', i, 'icon', e.target.value)}>
                              {iconNames.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className={lbl}>Description</label>
                            <textarea className={inp} rows={3} value={svc.description}
                              onChange={e => setItemField('services', i, 'description', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {draft.services.length === 0 && (
                    <div className="text-center py-12 text-slate-400">No services. Click "Add Service" to create one.</div>
                  )}
                </div>

                {/* Additional High-Value Services */}
                <div className="border-t border-slate-200 pt-8 mt-8">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">Additional High-Value Services</h3>
                      <p className="text-sm text-slate-500 mt-1">Add, edit, or delete services displayed in the dark-themed section at the bottom of the Services page.</p>
                    </div>
                    <button onClick={() => addItem('additionalServices', { title: 'New Additional Service', description: 'Brief description.', icon: 'Zap' })}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm text-white shadow-md transition-all"
                      style={{ background: draft.theme.primaryColor }}>
                      <Icons.Plus size={15} /> Add Additional Service
                    </button>
                  </div>
                  <div className="space-y-4">
                    {(draft.additionalServices || []).map((svc, i) => (
                      <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl relative group">
                        <button onClick={() => deleteItem('additionalServices', i)}
                          className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                          <Icons.Trash2 size={16} />
                        </button>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className={lbl}>Title</label>
                            <input className={inp} value={svc.title} onChange={e => setItemField('additionalServices', i, 'title', e.target.value)} />
                          </div>
                          <div>
                            <label className={lbl}>Icon</label>
                            <select className={inp} value={svc.icon} onChange={e => setItemField('additionalServices', i, 'icon', e.target.value)}>
                              {iconNames.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                            </select>
                          </div>
                          <div className="md:col-span-2">
                            <label className={lbl}>Description</label>
                            <textarea className={inp} rows={3} value={svc.description}
                              onChange={e => setItemField('additionalServices', i, 'description', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!draft.additionalServices || draft.additionalServices.length === 0) && (
                      <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">No additional services. Click "Add Additional Service" to create one.</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                PORTFOLIO TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'portfolio' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900">Portfolio</h2>
                    <p className="text-sm text-slate-500 mt-1">Manage the showcased projects with images, categories, and descriptions.</p>
                  </div>
                  <button onClick={() => addItem('portfolio', { title: 'New Project', category: 'Web', description: 'Project description.', image: '', tags: '' })}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm text-white shadow-md"
                    style={{ background: draft.theme.primaryColor }}>
                    <Icons.Plus size={15} /> Add Project
                  </button>
                </div>
                <div className="space-y-5">
                  {draft.portfolio.map((proj, i) => (
                    <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl relative group flex gap-5">
                      <button onClick={() => deleteItem('portfolio', i)}
                        className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                        <Icons.Trash2 size={16} />
                      </button>
                      {/* image upload */}
                      <label className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary shrink-0 overflow-hidden relative">
                        {proj.image
                          ? <img src={resolveUploadUrl(proj.image)} className="w-full h-full object-cover absolute inset-0" alt="" />
                          : <><Icons.ImagePlus size={24} className="text-slate-400 mb-1" /><span className="text-xs text-slate-400">Upload</span></>}
                        <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-bold opacity-0 hover:opacity-100 transition-all">Change</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'portfolio', i)} />
                      </label>
                      <div className="flex-1 grid md:grid-cols-2 gap-4">
                        <div>
                          <label className={lbl}>Title</label>
                          <input className={inp} value={proj.title} onChange={e => setItemField('portfolio', i, 'title', e.target.value)} />
                        </div>
                        <div>
                          <label className={lbl}>Category</label>
                          <input className={inp} value={proj.category} onChange={e => setItemField('portfolio', i, 'category', e.target.value)} />
                        </div>
                        <div className="md:col-span-2">
                          <label className={lbl}>Description</label>
                          <textarea className={inp} rows={2} value={proj.description}
                            onChange={e => setItemField('portfolio', i, 'description', e.target.value)} />
                        </div>
                        <div className="md:col-span-2">
                          <label className={lbl}>Tags (comma-separated)</label>
                          <input className={inp} value={proj.tags || ''} placeholder="e.g. Flutter, Dart, PHP"
                            onChange={e => setItemField('portfolio', i, 'tags', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {draft.portfolio.length === 0 && (
                    <div className="text-center py-12 text-slate-400">No projects yet. Click "Add Project" to start.</div>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                TESTIMONIALS TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'testimonials' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900">Testimonials</h2>
                    <p className="text-sm text-slate-500 mt-1">Manage client quotes, author info, and profile photos.</p>
                  </div>
                  <button onClick={() => addItem('testimonials', { quote: 'Great experience!', author: 'Client Name', role: 'CEO', company: '', image: '' })}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm text-white shadow-md"
                    style={{ background: draft.theme.primaryColor }}>
                    <Icons.Plus size={15} /> Add Testimonial
                  </button>
                </div>
                <div className="space-y-5">
                  {draft.testimonials.map((t, i) => (
                    <div key={i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl relative group flex gap-5">
                      <button onClick={() => deleteItem('testimonials', i)}
                        className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                        <Icons.Trash2 size={16} />
                      </button>
                      {/* avatar upload */}
                      <label className="w-24 h-24 rounded-full border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer hover:border-primary shrink-0 overflow-hidden relative">
                        {t.image
                        ? <img src={resolveUploadUrl(t.image)} className="w-full h-full object-cover absolute inset-0" alt="" />
                          : <><Icons.UserCircle size={28} className="text-slate-400 mb-1" /><span className="text-xs text-slate-400">Photo</span></>}
                        <span className="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-bold opacity-0 hover:opacity-100 transition-all rounded-full">Change</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'testimonials', i)} />
                      </label>
                      <div className="flex-1 grid md:grid-cols-3 gap-4">
                        <div>
                          <label className={lbl}>Author</label>
                          <input className={inp} value={t.author} onChange={e => setItemField('testimonials', i, 'author', e.target.value)} />
                        </div>
                        <div>
                          <label className={lbl}>Role</label>
                          <input className={inp} value={t.role} onChange={e => setItemField('testimonials', i, 'role', e.target.value)} />
                        </div>
                        <div>
                          <label className={lbl}>Company</label>
                          <input className={inp} value={t.company || ''} onChange={e => setItemField('testimonials', i, 'company', e.target.value)} />
                        </div>
                        <div className="md:col-span-3">
                          <label className={lbl}>Quote</label>
                          <textarea className={inp} rows={3} value={t.quote}
                            onChange={e => setItemField('testimonials', i, 'quote', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {draft.testimonials.length === 0 && (
                    <div className="text-center py-12 text-slate-400">No testimonials yet.</div>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                DYNAMIC PAGES TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'customPages' && (
              <div className="space-y-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-black text-slate-900">Dynamic Pages</h2>
                    <p className="text-sm text-slate-500 mt-1">Create custom content pages (FAQ, Careers, Terms…). Published pages auto-appear in the site navigation.</p>
                  </div>
                  <button onClick={() => addItem('customPages', {
                    id: Math.random().toString(36).slice(2),
                    slug: 'new-page', title: 'New Page',
                    content: '<h3>Page Title</h3><p>Edit this content in the dashboard.</p>',
                    isPublished: false })}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl font-bold text-sm text-white shadow-md"
                    style={{ background: draft.theme.primaryColor }}>
                    <Icons.Plus size={15} /> Create Page
                  </button>
                </div>
                <div className="space-y-5">
                  {draft.customPages.map((pg, i) => (
                    <div key={pg.id || i} className="p-5 bg-slate-50 border border-slate-200 rounded-2xl relative group space-y-4">
                      <button onClick={() => deleteItem('customPages', i)}
                        className="absolute top-4 right-4 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                        <Icons.Trash2 size={16} />
                      </button>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className={lbl}>Page Title</label>
                          <input className={inp} value={pg.title} onChange={e => setItemField('customPages', i, 'title', e.target.value)} />
                        </div>
                        <div>
                          <label className={lbl}>URL Slug</label>
                          <input className={inp + ' font-mono'} value={pg.slug}
                            onChange={e => setItemField('customPages', i, 'slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))} />
                        </div>
                        <div>
                          <label className={lbl}>Status</label>
                          <select className={inp}
                            value={pg.isPublished ? 'published' : 'draft'}
                            onChange={e => setItemField('customPages', i, 'isPublished', e.target.value === 'published')}>
                            <option value="draft">Draft (hidden)</option>
                            <option value="published">Published (in nav)</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={lbl}>Page Content (HTML allowed)</label>
                        <textarea className={inp + ' font-mono'} rows={6} value={pg.content}
                          placeholder="<h3>Section</h3><p>Your content here...</p>"
                          onChange={e => setItemField('customPages', i, 'content', e.target.value)} />
                      </div>
                      {pg.isPublished && (
                        <p className="text-xs text-slate-400">
                          Live at: <a href={`${PROJECT_ROOT}page/${pg.slug}`} target="_blank" rel="noreferrer"
                            className="underline" style={{ color: draft.theme.primaryColor }}>
                            /page/{pg.slug}
                          </a>
                        </p>
                      )}
                    </div>
                  ))}
                  {draft.customPages.length === 0 && (
                    <div className="text-center py-12 text-slate-400">No custom pages yet.</div>
                  )}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                FOOTER & CONTACTS TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'footerContacts' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Footer & Contacts</h2>
                  <p className="text-sm text-slate-500 mt-1">Edit tagline, copyright, contact info, socials, and footer navigation columns.</p>
                </div>

                {/* Footer meta */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800">Footer Settings</h3>
                  <div>
                    <label className={lbl}>Footer Tagline</label>
                    <input className={inp} value={draft.footer.tagline} onChange={e => setFooterField('tagline', e.target.value)} />
                  </div>
                  <div>
                    <label className={lbl}>Copyright Text</label>
                    <input className={inp} value={draft.footer.copyrightText} onChange={e => setFooterField('copyrightText', e.target.value)} />
                  </div>
                </div>

                {/* Contact info */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800">Agency Contact Info</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>Phone</label>
                      <input className={inp} value={draft.contact.phone} onChange={e => setContactField('phone', e.target.value)} />
                    </div>
                    <div>
                      <label className={lbl}>Email</label>
                      <input className={inp} value={draft.contact.email} onChange={e => setContactField('email', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Address</label>
                    <input className={inp} value={draft.contact.address} onChange={e => setContactField('address', e.target.value)} />
                  </div>

                  {/* Contact Hero Background Image Upload */}
                  <div className="pt-4 border-t border-slate-200">
                    <label className={lbl}>Contact Page Hero Background Image</label>
                    {draft.contact.backgroundImage ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                            <Icons.CheckCircle size={14} /> Custom background image active
                          </span>
                          <button
                            onClick={async () => {
                              if (window.confirm("Are you sure you want to remove the contact background image?")) {
                                const oldUrl = draft.contact.backgroundImage;
                                setContactField('backgroundImage', '');
                                
                                if (oldUrl?.startsWith('/biggrow/uploads/') || oldUrl?.startsWith('/uploads/')) {
                                  try {
                                    await adminFetch(`${API_BASE}/delete-image.php`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ url: oldUrl })
                                    });
                                  } catch (err) {
                                    console.error("Error deleting background image:", err);
                                  }
                                }
                              }
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-semibold rounded-lg transition border border-red-200 cursor-pointer"
                          >
                            <Icons.Trash2 size={12} /> Remove Image
                          </button>
                        </div>
                        <div className="h-28 max-w-sm rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative">
                          <img
                            src={resolveUploadUrl(draft.contact.backgroundImage)}
                            className="w-full h-full object-cover"
                            alt="Contact Background Preview"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-400">Currently using the default gradient background matching the active theme colors.</p>
                        <div className="flex items-center gap-3">
                          <input
                            type="file"
                            accept="image/*"
                            id="contact-bg-upload-input"
                            disabled={bgUploading}
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              setBgUploading(true);
                              setBgUploadError('');
                              
                              const formData = new FormData();
                              formData.append('image', file);
                              
                              try {
                                const response = await adminFetch(`${API_BASE}/upload-image.php`, {
                                  method: 'POST',
                                  body: formData
                                });
                                const data = await response.json();
                                if (response.ok && data.success) {
                                  setContactField('backgroundImage', data.url);
                                } else {
                                  setBgUploadError(data.error || 'Failed to upload image.');
                                }
                              } catch (err) {
                                console.error(err);
                                setBgUploadError('A network error occurred during upload.');
                              } finally {
                                setBgUploading(false);
                              }
                            }}
                          />
                          <label
                            htmlFor="contact-bg-upload-input"
                            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold rounded-xl shadow-sm transition-all cursor-pointer"
                          >
                            {bgUploading ? (
                              <><Icons.Loader2 className="animate-spin" size={13} /> Uploading...</>
                            ) : (
                              <><Icons.Upload size={13} /> Upload Background Image</>
                            )}
                          </label>
                        </div>
                        {bgUploadError && (
                          <p className="text-xs text-red-500 font-medium flex items-center gap-1 mt-1">
                            <Icons.AlertCircle size={12} /> {bgUploadError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Socials */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-slate-800">Social Media Links</h3>
                    <button onClick={addSocial}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-bold border transition-all"
                      style={{ color: draft.theme.primaryColor, borderColor: draft.theme.primaryColor + '44' }}>
                      <Icons.Plus size={13} /> Add Social
                    </button>
                  </div>
                  <div className="space-y-3">
                    {draft.contact.socials.map((s, i) => {
                      const Icon = adminSocialIconMap[s.platform] || Icons.Globe;
                      const colorClass = adminBrandColors[s.platform] || 'text-slate-400';
                      return (
                        <div key={i} className="flex gap-3 items-center">
                          <div className={`w-9 h-9 rounded-lg bg-white border border-slate-200/80 flex items-center justify-center ${colorClass} shrink-0 shadow-sm`}>
                            <Icon size={16} />
                          </div>
                          <select className="w-36 px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20"
                            value={s.platform}
                            onChange={e => setSocialField(i, 'platform', e.target.value)}>
                            {['LinkedIn','YouTube','Facebook','Twitter','Instagram','GitHub','Meta','TikTok','Pinterest'].map(p =>
                              <option key={p} value={p}>{p}</option>)}
                          </select>
                          <input className="flex-1 px-3 py-2 border border-slate-200 bg-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary/20"
                            value={s.url} placeholder="https://..."
                            onChange={e => setSocialField(i, 'url', e.target.value)} />
                          <button onClick={() => deleteSocial(i)} className="text-slate-400 hover:text-red-500 transition-all">
                            <Icons.Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer nav columns */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-slate-800">Footer Navigation Columns</h3>
                    <button onClick={addFooterSection}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-bold border transition-all"
                      style={{ color: draft.theme.primaryColor, borderColor: draft.theme.primaryColor + '44' }}>
                      <Icons.Plus size={13} /> Add Column
                    </button>
                  </div>
                  <div className="space-y-5">
                    {draft.footer.sections.map((sec, si) => (
                      <div key={si} className="p-4 bg-white border border-slate-200 rounded-xl relative group">
                        <button onClick={() => deleteFooterSection(si)}
                          className="absolute top-3 right-3 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                          <Icons.Trash2 size={15} />
                        </button>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-xs font-bold text-slate-500 uppercase">Column Title:</span>
                          <input className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-bold"
                            value={sec.title} onChange={e => setFooterSectionTitle(si, e.target.value)} />
                        </div>
                        <div className="space-y-2 pl-4 border-l-2 border-slate-100">
                          {sec.links.map((lk, li) => (
                            <div key={li} className="flex gap-2 items-center">
                              <input className="w-1/3 px-2 py-1.5 border border-slate-200 rounded-lg text-xs"
                                value={lk.label} placeholder="Label"
                                onChange={e => setFooterLinkField(si, li, 'label', e.target.value)} />
                              <input className="flex-1 px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-mono"
                                value={lk.href} placeholder="/path or https://..."
                                onChange={e => setFooterLinkField(si, li, 'href', e.target.value)} />
                              <button onClick={() => deleteFooterLink(si, li)} className="text-slate-400 hover:text-red-500 transition-all">
                                <Icons.X size={14} />
                              </button>
                            </div>
                          ))}
                          <button onClick={() => addFooterLink(si)}
                            className="text-xs font-semibold mt-1 flex items-center gap-1"
                            style={{ color: draft.theme.primaryColor }}>
                            <Icons.Plus size={12} /> Add Link
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                EMAIL / SMTP TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'emailSmtp' && (
              <div className="space-y-8">
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Email / SMTP Settings</h2>
                  <p className="text-sm text-slate-500 mt-1">Configure SMTP to receive contact form queries via email. All fields are required for email delivery.</p>
                </div>

                {/* Connection Settings */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Icons.Server size={16} /> SMTP Server</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className={lbl}>SMTP Host</label>
                      <input className={inp} value={draft.smtp.host} placeholder="smtp.gmail.com"
                        onChange={e => {
                          const smtp = { ...draft.smtp, host: e.target.value };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                    <div>
                      <label className={lbl}>Port</label>
                      <input className={inp} type="number" value={draft.smtp.port}
                        onChange={e => {
                          const smtp = { ...draft.smtp, port: parseInt(e.target.value) || 587 };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Encryption</label>
                    <select className={inp} value={draft.smtp.encryption}
                      onChange={e => {
                        const smtp = { ...draft.smtp, encryption: e.target.value as 'tls' | 'ssl' | 'none' };
                        commit({ ...draft, smtp });
                      }}>
                      <option value="tls">TLS (port 587 — recommended)</option>
                      <option value="ssl">SSL (port 465)</option>
                      <option value="none">None (port 25)</option>
                    </select>
                  </div>
                </div>

                {/* Auth Credentials */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Icons.Lock size={16} /> Authentication</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>SMTP Username / Email</label>
                      <input className={inp} value={draft.smtp.username} placeholder="your@gmail.com"
                        onChange={e => {
                          const smtp = { ...draft.smtp, username: e.target.value };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                    <div>
                      <label className={lbl}>SMTP Password / App Password</label>
                      <input className={inp} type="password" value={draft.smtp.password} placeholder="••••••••••••••••"
                        onChange={e => {
                          const smtp = { ...draft.smtp, password: e.target.value };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                  </div>
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs text-amber-700 flex items-start gap-2">
                      <Icons.AlertTriangle size={14} className="shrink-0 mt-0.5" />
                      <span><strong>Gmail users:</strong> Use an <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-bold">App Password</a> instead of your account password. Enable 2-Step Verification first.</span>
                    </p>
                  </div>
                </div>

                {/* Sender / Recipient */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Icons.Send size={16} /> Sender & Recipient</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className={lbl}>From Email</label>
                      <input className={inp} value={draft.smtp.fromEmail} placeholder="noreply@biggroww.com"
                        onChange={e => {
                          const smtp = { ...draft.smtp, fromEmail: e.target.value };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                    <div>
                      <label className={lbl}>From Name</label>
                      <input className={inp} value={draft.smtp.fromName} placeholder="BigGroww Website"
                        onChange={e => {
                          const smtp = { ...draft.smtp, fromName: e.target.value };
                          commit({ ...draft, smtp });
                        }} />
                    </div>
                  </div>
                  <div>
                    <label className={lbl}>Recipient Email (queries will be sent here)</label>
                    <input className={inp} value={draft.smtp.toEmail} placeholder="contact@biggroww.com"
                      onChange={e => {
                        const smtp = { ...draft.smtp, toEmail: e.target.value };
                        commit({ ...draft, smtp });
                      }} />
                  </div>
                </div>

                {/* Connection status indicator */}
                <div className="p-4 rounded-xl border flex items-center justify-between" style={{
                  borderColor: draft.smtp.host && draft.smtp.username && draft.smtp.toEmail ? '#bbf7d0' : '#fed7aa',
                  background: draft.smtp.host && draft.smtp.username && draft.smtp.toEmail ? '#f0fdf4' : '#fffbeb'
                }}>
                  <div className="flex items-center gap-3">
                    {draft.smtp.host && draft.smtp.username && draft.smtp.toEmail ? (
                      <>
                        <Icons.CheckCircle size={18} className="text-green-600" />
                        <span className="text-sm font-semibold text-green-700">SMTP configured — contact form queries will be emailed to <strong>{draft.smtp.toEmail}</strong></span>
                      </>
                    ) : (
                      <>
                        <Icons.AlertCircle size={18} className="text-amber-600" />
                        <span className="text-sm font-semibold text-amber-700">SMTP not fully configured — fill in all required fields above</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Send Test Email Card */}
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2"><Icons.MailCheck size={16} /> Test SMTP Configuration</h3>
                  <p className="text-xs text-slate-500">Send a test message using the configured settings to verify connectivity. <strong>Important: Save settings before testing!</strong></p>
                  
                  <div className="flex gap-3 max-w-lg">
                    <input
                      className={inp}
                      type="email"
                      value={testEmail}
                      onChange={e => setTestEmail(e.target.value)}
                      placeholder="test-recipient@example.com"
                      disabled={testStatus === 'loading'}
                    />
                    <button
                      onClick={handleSendTestEmail}
                      disabled={!testEmail || testStatus === 'loading'}
                      className="px-6 py-2.5 rounded-xl font-bold text-sm text-white shrink-0 shadow-sm transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center gap-1.5"
                      style={{ background: draft.theme.primaryColor }}
                    >
                      {testStatus === 'loading' ? (
                        <>
                          <Icons.Loader2 size={15} className="animate-spin" />
                          Testing...
                        </>
                      ) : (
                        'Send Test Email'
                      )}
                    </button>
                  </div>

                  {testStatus === 'success' && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl flex items-center gap-2">
                      <Icons.CheckCircle size={14} className="text-green-600 shrink-0" />
                      <span>Test email sent successfully! Check your inbox.</span>
                    </div>
                  )}

                  {testStatus === 'error' && (
                    <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2">
                      <Icons.AlertCircle size={14} className="text-rose-600 shrink-0" />
                      <span>{testError}</span>
                    </div>
                  )}
                </div>

              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
